import axios from "axios";
import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { API } from "../../utils/constants";
import { useAuth } from "../../utils/context";
import { parseJwtPayload } from "../../utils/parseJwtPayload";
import "./ImpersonationBanner.css";

const isExcludedPath = (pathname) => {
  if (
    pathname === "/login" ||
    pathname === "/signup" ||
    pathname === "/forgot-password"
  ) {
    return true;
  }
  if (pathname.startsWith("/reset_password/")) {
    return true;
  }
  return false;
};

const ImpersonationBanner = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [auth, setAuth] = useAuth();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const rawToken =
      auth?.accessToken?.token ||
      (typeof auth?.accessToken === "string" ? auth.accessToken : null);
    const payload = parseJwtPayload(rawToken);
    const isImpersonationToken = payload?.isImpersonation === true;

    setVisible(
      Boolean(isImpersonationToken) && !isExcludedPath(location.pathname)
    );
  }, [location.pathname, auth?.accessToken?.token, auth?.accessToken]);

  const handleReturnToAdmin = useCallback(async () => {
    try {
      await axios.post(`${API}/admin/stop-impersonation`);
    } catch (e) {
      console.warn("Failed to notify backend of stop impersonation:", e);
    } finally {
      const adminToken = localStorage.getItem("adminTokenBackup");

      if (!adminToken) {
        return;
      }

      try {
        const response = await fetch(`${API}/auth/validate-session`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${adminToken}`,
          },
        });

        if (!response.ok) {
          throw new Error("Admin backup session is invalid.");
        }

        const data = await response.json();
        const adminUser = data?.user;
        const isValidatedAdmin =
          adminUser?.isAdmin === true || adminUser?.role === "admin";

        if (!isValidatedAdmin) {
          throw new Error("Backup session does not belong to an admin.");
        }

        const restoredAuth = {
          user: adminUser,
          accessToken: {
            token: adminToken,
            expiry: Date.now() + 24 * 60 * 60 * 1000,
          },
        };

        setAuth(restoredAuth);
        localStorage.setItem("auth", JSON.stringify(restoredAuth));
        localStorage.removeItem("adminTokenBackup");
        localStorage.removeItem("adminUserBackup");
        localStorage.removeItem("isImpersonating");
        navigate("/admin-dashboard", { replace: true });
      } catch (err) {
        console.error("Failed to restore admin session:", err);
        localStorage.removeItem("adminTokenBackup");
        localStorage.removeItem("adminUserBackup");
        localStorage.removeItem("isImpersonating");
      }
    }
  }, [navigate, setAuth]);

  if (!visible) {
    return null;
  }

  return (
    <div
      className="impersonation-banner"
      role="region"
      aria-label="Admin impersonation active"
    >
      <div className="impersonation-banner__text">
        <p className="impersonation-banner__title">
          You are viewing this site as the selected student.
        </p>
        <p className="impersonation-banner__hint">
          Use &quot;Back to
          admin&quot; when you are done.
        </p>
      </div>
      <div className="impersonation-banner__right">
        <button
          type="button"
          className="impersonation-banner__btn"
          onClick={handleReturnToAdmin}
        >
          Back to admin
        </button>
        <button
          type="button"
          className="impersonation-banner__close"
          onClick={handleReturnToAdmin}
          aria-label="Close impersonation and return to admin"
          title="Close and return to admin"
        >
          <span aria-hidden="true">×</span>
        </button>
      </div>
    </div>
  );
};

export default ImpersonationBanner;
